import type { ListerBuyTrade, ListerCardFacts, ListerEdits, ListerMode, ListerProposal, ListerSchema, ListerSession } from "./api";
import { isValidGtin } from "./gtin";
import { CARD_CONDITIONS, type ListerCardCondition } from "./card-condition";
import { AUTOGRAPH_TYPES, type ListerAutographType } from "./card-autograph";

// M5 (send-to-lister review; Josh's rule — names and repeated values defined once): the note
// shown under every Buy / Trade-locked field (Review.tsx's Quantity and SKU, PriceCard's Cost)
// was three separate copies of the same literal.
export const BUY_TRADE_FIXED_NOTE = "Fixed by Buy / Trade";

// X2 (send-to-lister cross-app review; Josh's constants rule): hand-mirrors
// src/lister/pricing.ts's priceFloorCents exactly — ui/ does not import from src/ (ui/tsconfig
// .json's include is ["src", "../tests/ui"], and no file under ui/src crosses that boundary; see
// ui/src/lib/card-condition.ts's comment for the same pattern), so this is a second, hand-kept
// copy rather than a shared import. Kept honest by tests/ui/lister-price-card.test.ts, which
// imports the real server function directly and pins this to it — including the review's own
// $47.83-market-value example, where the unrounded cost × 1.25 these two copies used to disagree
// with the server over was exactly the floor a standard-rate item failed by a single cent.
// Replaces three separate inline copies of this same expression (this file's own clientValidate,
// and PriceCard.tsx's displayed "floor" line and its raise button).
export const priceFloorCents = (costCents: number): number => Math.ceil(Math.round(costCents * 1.25) / 100) * 100;

export type FormState = { proposal: ListerProposal; edits: ListerEdits; dirty: boolean; images: string[] };

export type Action =
  | { type: "init"; proposal: ListerProposal; edits: ListerEdits | null; images: string[] }
  | { type: "field"; key: "title" | "description_html" | "vendor" | "product_type" | "sku" | "barcode"; value: string }
  | { type: "number"; key: "price_cents" | "cost_cents" | "quantity" | "weight_lb"; value: number | null }
  | { type: "seo"; value: { title: string; description: string } }
  | { type: "tag.add"; value: string; isNew?: boolean }
  | { type: "tag.remove"; value: string }
  | { type: "tag.toggleNew"; value: string }
  | { type: "collection.toggle"; title: string }
  | { type: "attribute.set"; key: string; ids: string[]; names: string[] }
  | { type: "images.set"; urls: string[] }
  // New photographs arrived for this session. Refreshes what is AVAILABLE without touching
  // edits or `dirty`: an operator who already picked their images keeps that pick, and one who
  // has not yet picked falls through merged()'s `s.images.slice(0, 5)` default, so a product
  // that had no image at all comes back with the new photograph already selected.
  | { type: "images.available"; urls: string[] }
  | { type: "channel"; key: "online" | "pos" | "shop" | "headless" | "facebook"; value: boolean }
  | { type: "category"; id: string; full_name: string }
  | { type: "saved"; edits: ListerEdits };

export type Merged = ListerProposal & { price_cents: number; cost_cents: number | null; selected_images: string[]; accepted_new_collections: string[] };

const currentTags = (s: FormState): ListerProposal["tags"] => s.edits.tags ?? s.proposal.tags;

export function reducer(state: FormState, action: Action): FormState {
  const set = (edits: Partial<ListerEdits>): FormState => ({ ...state, edits: { ...state.edits, ...edits }, dirty: true });
  switch (action.type) {
    case "init":
      return { proposal: action.proposal, edits: action.edits ?? {}, dirty: false, images: action.images };
    case "field":
      return set({ [action.key]: action.key === "sku" && action.value === "" ? null : action.value } as Partial<ListerEdits>);
    case "number":
      if (action.key === "price_cents") return set({ price_cents: action.value ?? 0 });
      if (action.key === "cost_cents") return set({ cost_cents: action.value });
      return set({ [action.key]: action.value ?? 0 } as Partial<ListerEdits>);
    case "seo":
      return set({ seo: action.value });
    case "tag.add": {
      const v = action.value.trim();
      if (!v || currentTags(state).some((t) => t.value.toLowerCase() === v.toLowerCase())) return state;
      return set({ tags: [...currentTags(state), { value: v, is_new: action.isNew ?? true }] });
    }
    case "tag.remove":
      return set({ tags: currentTags(state).filter((t) => t.value !== action.value) });
    case "tag.toggleNew": {
      const rejected = new Set(state.edits.rejected_new_tags ?? []);
      if (rejected.has(action.value)) rejected.delete(action.value); else rejected.add(action.value);
      return set({ rejected_new_tags: [...rejected] });
    }
    case "collection.toggle": {
      const acc = new Set(state.edits.accepted_new_collections ?? []);
      if (acc.has(action.title)) acc.delete(action.title); else acc.add(action.title);
      return set({ accepted_new_collections: [...acc] });
    }
    case "attribute.set": {
      const base = state.edits.attributes ?? state.proposal.attributes;
      const exists = base.some((a) => a.key === action.key);
      const next = exists
        ? base.map((a) => (a.key === action.key ? { ...a, taxonomy_value_ids: action.ids, value_names: action.names } : a))
        : [...base, { key: action.key, name: action.key, taxonomy_value_ids: action.ids, value_names: action.names }];
      return set({ attributes: next });
    }
    case "images.set":
      return set({ selected_images: action.urls });
    case "images.available":
      return { ...state, images: action.urls };
    case "channel":
      return set({ channels: { ...(state.edits.channels ?? state.proposal.channels), [action.key]: action.value } });
    case "category":
      return set({ category: { id: action.id, full_name: action.full_name, reason: "operator", alternatives: [] }, attributes: [] });
    case "saved":
      // only clear dirty if nothing has changed the edits since the PUT that's being acknowledged
      // (a same-reference check, not deep-equal — a newer edit already replaced state.edits by then).
      return state.edits === action.edits ? { ...state, dirty: false } : state;
  }
}

export function merged(s: FormState, opts: { mode?: ListerMode; buy_trade?: Pick<ListerBuyTrade, "sku" | "unit_cost_cents" | "quantity" | "price_cents"> | null } = {}): Merged {
  const { price_cents, cost_cents, selected_images, accepted_new_collections, rejected_new_tags, ...rest } = s.edits;
  // A card is one unit. normalizeProposal already forces quantity: 1 for card mode server-side,
  // but an edit spread over the proposal here would silently undo it in the browser's own view
  // — mirrors src/lister/validate.ts's mergeProposal so the two never disagree.
  if (opts.mode === "card") delete (rest as Partial<ListerProposal>).quantity;
  // Buy / Trade's facts win here too, mirroring mergeProposal's server-side lock: an edit to
  // sku or quantity must never survive onto the proposal the Review page shows.
  if (opts.buy_trade) { delete (rest as Partial<ListerProposal>).sku; delete (rest as Partial<ListerProposal>).quantity; }
  const base = { ...s.proposal, ...rest } as ListerProposal;
  const rejected = new Set((rejected_new_tags ?? []).map((t) => t.toLowerCase()));
  return {
    ...base,
    tags: base.tags.filter((t) => !(t.is_new && rejected.has(t.value.toLowerCase()))),
    // The starting price is Buy / Trade's own price until an edit (including picking the
    // research price) says otherwise — mirrors mergeProposal exactly.
    price_cents: price_cents ?? (opts.buy_trade ? opts.buy_trade.price_cents : s.proposal.price.suggested_cents),
    // Buy / Trade's cost always wins, never an edit.
    cost_cents: opts.buy_trade ? opts.buy_trade.unit_cost_cents : (cost_cents ?? null),
    selected_images: selected_images ?? s.images.slice(0, 5),
    accepted_new_collections: accepted_new_collections ?? [],
  };
}

export function clientValidate(m: Merged, schema: ListerSchema | null, images: string[], opts: { mode?: ListerMode; card?: { is_raw: boolean; condition: ListerCardCondition | null; has_autograph: boolean; autograph_type: ListerAutographType | null } } = {}): Array<{ field: string; message: string }> {
  const issues: Array<{ field: string; message: string }> = [];
  const add = (field: string, message: string) => issues.push({ field, message });
  if (!schema || schema.category.id !== m.category.id) add("category", "Loading fields for this category…");
  else if (!schema.category.is_leaf) add("category", "Pick a leaf category");
  else {
    for (const attr of m.attributes) {
      const sa = schema.attributes.filter((a) => a.metafield_key === attr.key);
      if (sa.length === 0) { add(`attributes.${attr.key}`, "Not a field of this category"); continue; }
      const allowed = new Set(sa.flatMap((a) => a.values.map((v) => v.taxonomy_value_id)));
      if (attr.taxonomy_value_ids.some((id) => !allowed.has(id))) add(`attributes.${attr.key}`, "Value not allowed");
    }
  }
  if (m.title.trim().length < 10 || m.title.length > 255) add("title", "10–255 characters");
  if (!m.vendor.trim()) add("vendor", "Required");
  if (!m.product_type.trim()) add("product_type", "Required");
  if (!(m.price_cents > 0)) add("price_cents", "Must be more than $0");
  if (m.cost_cents !== null && m.cost_cents > 0) {
    const floorCents = priceFloorCents(m.cost_cents);
    if (m.price_cents < floorCents) add("price_cents", `Below the cost × 1.25 floor ($${(floorCents / 100).toFixed(2)})`);
  }
  if (!(m.weight_lb > 0)) add("weight_lb", "Must be more than 0");
  if (!Number.isInteger(m.quantity) || m.quantity < 0) add("quantity", "Whole number ≥ 0");
  if (m.selected_images.length === 0 || m.selected_images.some((u) => !images.includes(u))) add("selected_images", "Pick at least one image");
  // A graded card's barcode carries the certificate number (or stays empty when there is
  // none) and an item session's is Buy / Trade's in-store barcode — neither has a check-digit
  // format to validate against, so any value is accepted. Mirrors validateMerged: only a
  // barcode session (the default) is held to the GTIN check digit.
  if ((opts.mode ?? "barcode") === "barcode" && !isValidGtin(m.barcode)) add("barcode", "Fails the check digit");
  // Mirrors src/lister/validate.ts's raw rule so the operator sees the issue beside the
  // control instead of after a failed post. The SERVER is the authority — this is a
  // convenience, and POST /post rejects a raw card with no condition regardless.
  if (opts.card?.is_raw && !CARD_CONDITIONS.includes(opts.card.condition as never)) add("card_condition", "Pick the card's condition");
  // Mirrors src/lister/validate.ts's autograph rule so the operator sees the issue beside the
  // control instead of after a failed post. The SERVER is the authority — POST /post rejects a
  // flagged card with no placement regardless of what this says.
  if (opts.card?.has_autograph && !AUTOGRAPH_TYPES.includes(opts.card.autograph_type as never)) add("card_autograph_type", "Say where the autograph is");
  return issues;
}

/** The card's own title, built from the reconciled facts — the same field order the server's
 *  cardTitle uses (src/lister/card/identity.ts), so the history row and the review header read
 *  the same. MUST match cardTitle exactly: tests/ui/lister-card-title.test.ts pins both to one
 *  shared fixture, and an earlier review caught this drifting once already. */
export function cardFactsTitle(f: ListerCardFacts): string {
  return [
    f.year,
    f.set,
    f.number ? `#${f.number}` : null,
    f.player && f.player !== f.name ? f.player : null,
    f.name,
    f.parallel || null,
    f.serial,
    f.grader && f.grade ? `${f.grader} ${f.grade}` : null,
  ].filter((x): x is string => Boolean(x && x.trim())).join(" ").replace(/\s+/g, " ").trim();
}

// X1 (send-to-lister cross-app review): hand-mirrors src/lister/buy-trade.ts's
// BUY_TRADE_FACTORY_SEALED_CONDITION and BUY_TRADE_SEALED_CONDITIONS exactly, the same way
// priceFloorCents above mirrors pricing.ts (ui/ does not import from src/). Kept honest by
// tests/ui/lister-state.test.ts, which imports the real server constants directly.
const BUY_TRADE_FACTORY_SEALED_CONDITION = "Factory sealed";
const BUY_TRADE_SEALED_CONDITIONS = [BUY_TRADE_FACTORY_SEALED_CONDITION, "Sealed, wrap damage", "Opened"] as const;

/** A sealed box Buy / Trade itself recorded as opened or wrap-damaged must never read as
 *  factory sealed on the Review page (X1) — the Map pass is already told this server-side
 *  (src/lister/pipeline.ts's mapStage), and this is the same check for the badge. Returns null
 *  (nothing to flag) for "Factory sealed", for null, and for a RAW CARD's own condition label
 *  (e.g. "Near mint") — a completely different vocabulary this set never contains. */
export function buyTradeConditionFlag(condition: string | null): string | null {
  if (!condition || condition === BUY_TRADE_FACTORY_SEALED_CONDITION) return null;
  if (!(BUY_TRADE_SEALED_CONDITIONS as readonly string[]).includes(condition)) return null;
  return `Buy / Trade recorded this unit as "${condition}", not factory sealed.`;
}

/** send-to-lister review I2(b): Buy / Trade's own grader/grade/cert against the reconciled
 *  card facts the session's own photographs (and cert lookup) settled on — never the other way
 *  round; this never overrides `facts`, it only names a difference for the operator to check.
 *  A field counts as differing only when BOTH sides stated one and they disagree: a null on
 *  either side is "didn't say", not a contradiction (the same bias `reconcileCertAndRead` and
 *  `readVsGraderConflicts` use). Grader and grade are reported together, as a pair, the way the
 *  operator actually reads a label ("SGC 9.5") — cert is its own number and reported on its own.
 *  Returns null (no warning) when there is nothing to compare, or nothing differs. */
export function buyTradeCertMismatch(
  bt: Pick<ListerBuyTrade, "grader" | "grade" | "cert"> | null,
  facts: Pick<ListerCardFacts, "grader" | "grade" | "cert"> | null,
): string | null {
  if (!bt || !facts) return null;
  const graderOrGrade = (g: string | null, n: string | null): string => [g, n].filter(Boolean).join(" ");
  const gradeDiffers = Boolean((bt.grader && facts.grader && bt.grader !== facts.grader) || (bt.grade && facts.grade && bt.grade !== facts.grade));
  const certDiffers = Boolean(bt.cert && facts.cert && bt.cert !== facts.cert);
  if (!gradeDiffers && !certDiffers) return null;
  const side = (grader: string | null, grade: string | null, cert: string | null): string =>
    [gradeDiffers ? graderOrGrade(grader, grade) : null, certDiffers ? `cert ${cert}` : null].filter(Boolean).join(", ");
  return `Buy / Trade says ${side(bt.grader, bt.grade, bt.cert)}; the photos read ${side(facts.grader, facts.grade, facts.cert)}. Check the label.`;
}

/** What to show where a session's GTIN used to go. Never an empty string. */
export function sessionLabel(s: ListerSession): string {
  if (s.gtin) return s.gtin;
  const fromCert = s.cert ? cardFactsTitle(s.cert.facts) : "";
  return s.proposal?.title ?? s.identity?.official_title ?? (fromCert || (s.mode === "item" ? "item" : "card"));
}
